/**
 * « C'est vu en classe » — la déclaration d'un parent, et sa confirmation.
 *
 * Hedi, le 25 septembre 2026 : *« Quand tu me dis pour un exercice "pas encore
 * vu en classe" je veux que tu me donne la compétence pas encore vue. Je veux
 * pouvoir cliquer dessus, avoir un message de validation pour marquer cette
 * compétence comme vue en classe. »* La mention existait et ne menait nulle
 * part : le parent lisait un avertissement sur l'écran où il compose, et devait
 * quitter la V2 pour l'écran « Ce qui a été vu » afin d'y répondre.
 *
 * **La confirmation est rendue dans la page, jamais par `window.confirm`.** Le
 * `WebChromeClient` de la coquille Android ne surcharge que `onShowFileChooser` :
 * une boîte du navigateur y est un refus silencieux, c'est-à-dire un bouton qui
 * ne fait rien sur le seul appareil où le système tourne.
 *
 * **Elle nomme la compétence.** « Vous confirmez ? » ne dit pas *quoi*, et le
 * parent ne peut alors pas vérifier qu'il a cliqué sur ce qu'il croyait — la
 * même raison qui fait nommer le chapitre dans le message de l'enfant
 * (`ui/PasEncoreVu`).
 *
 * **Et elle dit ce que la déclaration emporte.** Elle ouvre le chapitre au
 * travail du soir *et* elle lève un « on n'a pas encore vu ça » que l'enfant
 * aurait posé dessus : un parent qui coche décide donc contre son enfant, et
 * c'est à savoir avant de cliquer et non après. Elle ne mesure rien (règle 3).
 *
 * **Tout est en contenu de phrase** — des `span` et des `button`, aucun `p` ni
 * `div` — pour que le composant soit posable au milieu d'une ligne de mentions,
 * qui est un paragraphe : un bloc dans un `<p>` le referme, et la suite de la
 * ligne part ailleurs dans la page.
 *
 * Il vit dans `ui/` parce que le geste est le même sur deux coquilles — la V2 et
 * l'écran de détail d'un enfant — et que `v2/` n'a pas le droit d'importer
 * `parent/`, la règle que `ui/periode.ts` puis `ui/planDuSoir.ts` ont déjà
 * appliquée. Deux boutons pour une écriture finiraient par ne plus dire la même
 * chose de ce qu'elle emporte.
 */

import { useState } from "react";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import { Button } from "@/ui/primitives";

type Etat = "ferme" | "confirme" | "envoi" | "fait" | "panne";

/**
 * La phrase de confirmation, nommant la compétence.
 *
 * Séparée du composant pour être lisible sans DOM, comme `confirmation` dans
 * `ui/PasEncoreVu`.
 */
export function demande(libelle: string): string {
  return `Marquer « ${libelle} » comme vue en classe ?`;
}

export function VuEnClasse({
  childId,
  competencyId,
  libelle,
  intitule = "Oui, c'est vu en classe",
  discret = false,
  onDeclare,
}: {
  childId: string;
  competencyId: string;
  libelle: string;
  /** Ce que porte le déclencheur. La mention de la V2 est déjà une phrase —
   *  « pas encore vu en classe » —, et la doubler d'un bouton nommé autrement
   *  ferait deux choses à l'écran là où il n'y a qu'un geste. */
  intitule?: string;
  /** Posé en ligne, dans une suite de mentions, plutôt qu'en bouton détaché. */
  discret?: boolean;
  /** Relire après coup : l'appelant sait ce que la déclaration a rendu périmé
   *  chez lui. Retirer la ligne localement afficherait moins que ce qui s'est
   *  passé — la compétence revient attestée, avec sa raison. */
  onDeclare?: () => void | Promise<void>;
}) {
  const [etat, setEtat] = useState<Etat>("ferme");
  const [panne, setPanne] = useState("");

  const declarer = async () => {
    setEtat("envoi");
    try {
      await call("vuEnClasseDeclarer", {
        params: { child_id: childId },
        body: { competency_ids: [competencyId], vu: true },
      });
      setEtat("fait");
      await onDeclare?.();
    } catch (echec) {
      setPanne(echec instanceof ApiError ? echec.message : "La déclaration n'est pas partie.");
      setEtat("panne");
    }
  };

  // Le plus souvent invisible : l'appelant relit, et la ligne change d'état ou
  // disparaît. Écrite quand même, pour la fenêtre entre les deux et pour le cas
  // où l'appelant ne relit rien.
  if (etat === "fait") {
    return (
      <span className="vu-classe vu-classe--fait">
        C'est noté : « {libelle} » est vue en classe.
      </span>
    );
  }

  return (
    <span className={discret ? "vu-classe vu-classe--discret" : "vu-classe"}>
      {etat === "ferme" ? (
        <button
          type="button"
          className={discret ? "vu-classe__declencheur" : "btn btn--secondary"}
          onClick={() => setEtat("confirme")}
        >
          {intitule}
        </button>
      ) : null}

      {etat === "confirme" || etat === "envoi" ? (
        <span className="vu-classe__confirmation">
          <span className="vu-classe__demande">{demande(libelle)}</span>
          <span className="vu-classe__portee">
            Le chapitre s'ouvrira au travail du soir, et un « on n'a pas encore vu ça » déclaré par
            l'enfant sera levé. Rien n'est mesuré.
          </span>
          <span className="vu-classe__actions">
            <Button onClick={declarer} disabled={etat === "envoi"}>
              {etat === "envoi" ? "Enregistrement…" : "Oui, c'est vu en classe"}
            </Button>
            <Button variant="quiet" onClick={() => setEtat("ferme")} disabled={etat === "envoi"}>
              Annuler
            </Button>
          </span>
        </span>
      ) : null}

      {etat === "panne" ? <span className="vu-classe__panne">{panne}</span> : null}
    </span>
  );
}
